import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import RequiredIndicator from '@/components/common/RequiredIndicator';
import { Spinner } from '@/components/common/Spinner';
import { ConfigContext } from '@/Contexts/ConfigContext';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { SelectedPartnerContext } from '@/Contexts/SelectedPartnerContext';
import { SelectedProgramContext } from '@/Contexts/SelectedProgramContext';
import usePartner from '@/hooks/usePartner';
import { PartnerProps } from '@/models/Partner';
import { PartnerSubCategoryProps } from '@/models/PartnerSubCategory';
import PartnerRowWrap from '../../application_components/question_types/PartnerRowWrap';
import { TabSubPanelContent } from '@/components/myaccount/Applicant_MyAccount/TabSubPanelContent';

const CategorySelect = ({
  text,
  partnerInfoLocal,
  disabled,
  required,
  onChange,
}: {
  text: string;
  partnerInfoLocal: PartnerProps;
  setPartnerInfoLocal: React.Dispatch<React.SetStateAction<PartnerProps | null>>;
  disabled: boolean;
  required: boolean;
  onChange: (key: keyof PartnerProps, e: SelectChangeEvent<string>) => void;
}) => {
  const { partnerSubCategories } = useContext(ConfigContext);
  const { selectedProgramInfo } = useContext(SelectedProgramContext);

  const [partnerSubCategoryOptions, setPartnerSubCategoryOptions] = useState<
    PartnerSubCategoryProps[] | undefined
  >([]);

  useEffect(() => {
    if (
      (partnerSubCategoryOptions ?? []).length > 0 ||
      selectedProgramInfo?.partnerSubCategories == null
    )
      return;

    const selectedPartnerSubCategory =
      partnerInfoLocal?.partnerSubCategoryId != null
        ? partnerSubCategories.find(
            (psc: PartnerSubCategoryProps) => psc.id == partnerInfoLocal?.partnerSubCategoryId
          )
        : null;

    let partnerSubCategoryOptionList = selectedProgramInfo.partnerSubCategories;
    if (
      selectedPartnerSubCategory != null &&
      partnerSubCategoryOptionList.find(
        (psc: PartnerSubCategoryProps) => psc.id == selectedPartnerSubCategory.id
      ) == null
    ) {
      partnerSubCategoryOptionList = [...partnerSubCategoryOptionList, selectedPartnerSubCategory];
    }

    setPartnerSubCategoryOptions(partnerSubCategoryOptionList);
  }, [
    partnerInfoLocal?.partnerSubCategoryId,
    partnerSubCategories,
    partnerSubCategoryOptions,
    selectedProgramInfo?.partnerSubCategories,
  ]);

  const handleCategoryChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      event.preventDefault();
      event.stopPropagation();

      if (onChange != null) onChange('partnerSubCategoryId', event);
    },
    [onChange]
  );

  return (
    <FormControl variant='outlined' fullWidth required={required}>
      <InputLabel id='partner-sub-category-label'>{text}</InputLabel>
      <Select
        labelId='partner-sub-category-label'
        id='partner-sub-category-select'
        value={
          partnerInfoLocal?.partnerSubCategoryId != null && partnerSubCategories?.length > 0
            ? partnerInfoLocal?.partnerSubCategoryId?.toString()
            : ''
        }
        defaultValue='Select'
        disabled={disabled}
        onChange={handleCategoryChange}
      >
        {partnerSubCategoryOptions?.map((category: PartnerSubCategoryProps) => {
          return (
            <MenuItem key={category.id} value={category.id}>
              {category.subCategory}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

const TextRow = ({
  label,
  value,
  onChange,
  selectOnFocus,
  id,
  style,
  required,
  loading,
  disabled,
}: {
  label: string;
  value?: string | number | null;
  onChange?: (
    key: keyof PartnerProps,
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  selectOnFocus?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  id?: string;
  style?: React.CSSProperties;
  required?: boolean;
  loading?: boolean;
  disabled?: boolean;
}) => {
  const { partnerInfo, updatePartnerInfo } = useContext(SelectedPartnerContext);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.target.value.length > 0) {
      let key = id;
      if (id == 'partner') {
        updatePartnerInfo({
          ...partnerInfo,
          partnerName: e.target.value,
        });
        key = 'partnerName';
      }
      if (label.includes('Phone Number for') || label.includes('Main School')) {
        key = 'genericPhone';
      }

      if (onChange != null && key != null) onChange(key as keyof PartnerProps, e);
    }
  };

  return (
    <TextField
      style={style}
      onFocus={selectOnFocus}
      onChange={handleChange}
      defaultValue={value}
      fullWidth={id != null && id.includes('address') ? true : false}
      sx={{ flexGrow: 1 }}
      required={required}
      error={required && value == null}
      disabled={loading || disabled}
      id={id}
      type={id == 'email' ? 'email' : 'text'}
      label={label}
      className={label}
      inputProps={{}}
      InputProps={{
        endAdornment: loading && <Spinner type='circular' size={20} />,
      }}
      variant='outlined'
    />
  );
};

function MyAccountPartnerDetails({
  onUnsavedChanges,
  onSave,
}: { onUnsavedChanges?: () => void; onSave?: () => void } = {}) {
  const { postPartner } = usePartner();

  const { partnerInfo, partnerGroupInfo, updatePartnerInfo } = useContext(SelectedPartnerContext);
  const { selectedProgramInfo } = useContext(SelectedProgramContext);

  const [partnerInfoLocal, setPartnerInfoLocal] = useState<PartnerProps | null>(null);

  const { translatedText } = useContext(LocaleContext);

  const [postingPartner, setPostingPartner] = useState(false);

  const requiredFields = useMemo<Array<keyof PartnerProps>>(
    () => ['partnerName', 'lowestGrade', 'highestGrade', 'partnerSubCategoryId'],
    []
  );

  function selectOnFocus(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) {
    e.target.select();
  }

  const handleChange = useCallback(
    (
      key: keyof PartnerProps,
      e: SelectChangeEvent<string> | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      if (partnerInfoLocal == null) return;
      const val = e.target.value == '' ? null : e.target.value;

      if (partnerInfoLocal[key] != e.target.value) {
        setPartnerInfoLocal({
          ...partnerInfoLocal,
          [key]: val,
        });
        onUnsavedChanges?.();
      }
    },
    [partnerInfoLocal, onUnsavedChanges]
  );

  const handleSave = useCallback(async () => {
    if (partnerInfoLocal == null) return;
    setPostingPartner(true);
    const updatedPartner = await postPartner(partnerInfoLocal);
    updatePartnerInfo(updatedPartner);
    setPostingPartner(false);
    onSave?.();
  }, [onSave, partnerInfoLocal, postPartner, updatePartnerInfo]);

  useEffect(() => {
    setPartnerInfoLocal((prevState) => ({
      ...prevState,
      ...partnerInfo,
    }));
  }, [partnerInfo]);

  return (
    <TabSubPanelContent
      headerText={`${selectedProgramInfo?.partnerTerm} ${translatedText.signup.headerLabel}`}
      onSave={handleSave}
      saveDisabled={postingPartner}
      isPanelLoading={partnerInfoLocal == null}
    >
      {partnerInfoLocal != null && (
        <Box
          display='flex'
          justifyContent='space-between'
          flexDirection='column'
          height='auto'
          gap='15px'
        >
          {selectedProgramInfo != null && selectedProgramInfo.displayCategory && (
            <PartnerRowWrap>
              <CategorySelect
                text={`${translatedText?.signup.labelCategory} ${selectedProgramInfo?.partnerTerm}?`}
                partnerInfoLocal={partnerInfoLocal}
                setPartnerInfoLocal={setPartnerInfoLocal}
                disabled={postingPartner}
                required={requiredFields.includes('partnerSubCategoryId')}
                onChange={handleChange}
              />
            </PartnerRowWrap>
          )}
          <PartnerRowWrap>
            <TextRow
              key={partnerInfoLocal.id}
              value={partnerInfoLocal.partnerName}
              required={requiredFields.includes('partnerName')}
              label={`${selectedProgramInfo?.partnerTerm} ${translatedText.signup.labelName}`}
              onChange={handleChange}
              selectOnFocus={selectOnFocus}
              id={'partner'}
              loading={postingPartner}
            />
          </PartnerRowWrap>

          {partnerInfoLocal?.schoolDistrict && (
            <PartnerRowWrap>
              <TextRow
                key={partnerGroupInfo?.id}
                value={partnerGroupInfo?.partnerName}
                label={`School District Name`}
                disabled
                id={'partnerGroupName'}
              />
            </PartnerRowWrap>
          )}

          {partnerInfoLocal?.healthcareSystem && (
            <PartnerRowWrap>
              <TextRow
                key={partnerGroupInfo?.id}
                value={partnerGroupInfo?.partnerName}
                label={`Healthcare System Name`}
                disabled
                id={'partnerGroupName'}
              />
            </PartnerRowWrap>
          )}

          {selectedProgramInfo?.displayLowestGrade && (
            <>
              <PartnerRowWrap>
                <FormControl sx={{ flexGrow: 1, marginTop: '10px' }} size='small'>
                  <InputLabel id='lowest-grade-label'>
                    Lowest Grade <RequiredIndicator />
                  </InputLabel>
                  <Select
                    labelId='lowest-grade-label'
                    id='lowestGrade'
                    variant='outlined'
                    required={requiredFields.includes('lowestGrade')}
                    value={
                      partnerInfoLocal?.lowestGrade != null
                        ? partnerInfoLocal.lowestGrade.toString()
                        : ''
                    }
                    onChange={(e: SelectChangeEvent<string>) => handleChange('lowestGrade', e)}
                    disabled={postingPartner}
                    inputProps={{
                      endAdornment: postingPartner && <Spinner type='circular' size={20} />,
                    }}
                  >
                    <MenuItem value={'-1'}>Pre-K</MenuItem>
                    <MenuItem value={'0'}>Kindergarten</MenuItem>
                    <MenuItem value={'1'}>1st</MenuItem>
                    <MenuItem value={'2'}>2nd</MenuItem>
                    <MenuItem value={'3'}>3rd</MenuItem>
                    <MenuItem value={'4'}>4th</MenuItem>
                    <MenuItem value={'5'}>5th</MenuItem>
                    <MenuItem value={'6'}>6th</MenuItem>
                    <MenuItem value={'7'}>7th</MenuItem>
                    <MenuItem value={'8'}>8th</MenuItem>
                    <MenuItem value={'9'}>9th</MenuItem>
                    <MenuItem value={'10'}>10th</MenuItem>
                    <MenuItem value={'11'}>11th</MenuItem>
                    <MenuItem value={'12'}>12th</MenuItem>
                  </Select>
                </FormControl>

                <FormControl sx={{ flexGrow: 1, marginTop: '10px' }} size='small'>
                  <InputLabel id='highest-grade-select-label'>
                    Highest Grade <RequiredIndicator />
                  </InputLabel>
                  <Select
                    labelId='highest-grade-select-label'
                    id='highest-grade-select'
                    variant='outlined'
                    required={requiredFields.includes('highestGrade')}
                    value={
                      partnerInfoLocal?.highestGrade != null
                        ? partnerInfoLocal.highestGrade.toString()
                        : ''
                    }
                    key={'highestGrade'}
                    onChange={(e: SelectChangeEvent<string>) => handleChange('highestGrade', e)}
                    disabled={postingPartner}
                    inputProps={{
                      endAdornment: postingPartner && <Spinner type='circular' size={20} />,
                    }}
                  >
                    <MenuItem value={'-1'}>Pre-K</MenuItem>
                    <MenuItem value={'0'}>Kindergarten</MenuItem>
                    <MenuItem value={'1'}>1st</MenuItem>
                    <MenuItem value={'2'}>2nd</MenuItem>
                    <MenuItem value={'3'}>3rd</MenuItem>
                    <MenuItem value={'4'}>4th</MenuItem>
                    <MenuItem value={'5'}>5th</MenuItem>
                    <MenuItem value={'6'}>6th</MenuItem>
                    <MenuItem value={'7'}>7th</MenuItem>
                    <MenuItem value={'8'}>8th</MenuItem>
                    <MenuItem value={'9'}>9th</MenuItem>
                    <MenuItem value={'10'}>10th</MenuItem>
                    <MenuItem value={'11'}>11th</MenuItem>
                    <MenuItem value={'12'}>12th</MenuItem>
                  </Select>
                </FormControl>
              </PartnerRowWrap>
            </>
          )}
        </Box>
      )}
    </TabSubPanelContent>
  );
}

export default MyAccountPartnerDetails;
